import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Hammer, Home, Camera, ImagePlus, ArrowRight, Star, X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Local Trust section — real people, real work. Designed for both the Homepage
 * and the About page. Owner intro on the left + a 3-category "Recent Work"
 * gallery on the right (or below on mobile). Every image is a clean placeholder
 * with an obvious "swap me" affordance until real photos are dropped in.
 *
 * To swap a placeholder for a real image later, edit the RECENT_WORK array
 * below and set `image` to a public URL or an imported asset. That's it.
 */

const OWNER_PORTRAIT = null; // TODO: set to /images/owner.jpg once uploaded

const RECENT_WORK = [
  {
    id: "post-construction",
    icon: Sparkles,
    title: "Post-Construction Deep Cleans",
    blurb: "5-bedroom new-build handoffs, top-to-bottom — spotless mahogany floors, dust-free surfaces, move-in ready.",
    photos: ["/projects/post-construction-1.jpg", "/projects/post-construction-2.jpg"],
  },
  {
    id: "flooring",
    icon: Home,
    title: "Custom Flooring Installations",
    blurb: "Luxury vinyl plank, laminate, and hardwood — clean seams, straight baseboards, honest craftsmanship.",
    photos: [
      "/projects/flooring-1.webp",
      "/projects/flooring-2.webp",
      "/projects/flooring-3.webp",
      "/projects/flooring-4.webp",
    ],
  },
  {
    id: "decks-fences",
    icon: Hammer,
    title: "Fence Builds & Repairs",
    blurb: "From full privacy-fence installs to AC-unit enclosures — clean cedar, solid posts, level every time.",
    photos: [
      "/projects/fence-5.webp",  // Finished — hero shot with worker
      "/projects/fence-3.webp",  // Finished — driveway angle
      "/projects/fence-4.webp",  // Finished — backyard angle
      "/projects/fence-2.webp",  // In progress — framing + boards
      "/projects/fence-1.webp",  // In progress — early posts
    ],
  },
];

function ProjectLightbox({ project, onClose }) {
  const [index, setIndex] = useState(0);
  const photos = project?.photos || [];
  const total = photos.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (!project) return;
    setIndex(0);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [project, next, prev, onClose]);

  if (!project) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/85 backdrop-blur-sm"
      onClick={onClose}
      data-testid="project-lightbox"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between text-white mb-3 px-1">
          <div>
            <div className="font-extrabold text-lg md:text-xl leading-tight" style={{ fontFamily: "Outfit" }}>{project.title}</div>
            <div className="text-xs md:text-sm text-white/60">{project.blurb}</div>
          </div>
          {total > 1 && (
            <div className="text-xs md:text-sm text-white/70 flex-shrink-0 ml-3 font-mono">
              {index + 1} / {total}
            </div>
          )}
        </div>

        <div className="relative aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden bg-black">
          <img
            src={photos[index]}
            alt={`${project.title} — photo ${index + 1}`}
            className="w-full h-full object-contain"
            data-testid={`lightbox-image-${index}`}
          />

          {total > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous photo"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-[var(--brand-teal)] flex items-center justify-center shadow-lg transition"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next photo"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-[var(--brand-teal)] flex items-center justify-center shadow-lg transition"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {total > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {photos.map((src, i) => (
              <button
                key={src}
                onClick={() => setIndex(i)}
                aria-label={`Show photo ${i + 1}`}
                className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition ${
                  i === index ? "border-[var(--brand-gold)] ring-2 ring-[var(--brand-gold)]/40" : "border-white/20 opacity-60 hover:opacity-100"
                }`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PhotoTile({ photos, alt, testid }) {
  if (photos && photos.length > 0) {
    return (
      <>
        <img
          src={photos[0]}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          data-testid={testid}
        />
        {photos.length > 1 && (
          <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/70 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wider">
            <Camera className="w-3 h-3" /> +{photos.length - 1} more
          </div>
        )}
      </>
    );
  }
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[var(--brand-cream-soft)] to-[#e6e0d4] text-[var(--brand-teal)] p-4 text-center"
      data-testid={testid}
    >
      <ImagePlus className="w-8 h-8 mb-2 opacity-60" />
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Photo Coming Soon</div>
    </div>
  );
}

export default function LocalTrust({ variant = "home" }) {
  const isHome = variant === "home";
  const [openProject, setOpenProject] = useState(null);
  return (
    <section
      className={`${isHome ? "py-20 md:py-24" : "py-14"} bg-white`}
      data-testid={`local-trust-${variant}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {isHome && (
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-[var(--brand-gold)] mb-3">
              <span className="gold-divider" />
              <span className="section-eyebrow">Real People. Real Work.</span>
              <span className="gold-divider" />
            </div>
            <h2 className="heading-display text-3xl md:text-4xl">Local, Family-Run, and Proud of Every Job</h2>
            <p className="mt-4 max-w-2xl mx-auto text-slate-600 leading-relaxed">
              We're your neighbors in Evansville — a small local team that takes on every job like it's our own home.
              No call centers, no bait-and-switch pricing, just steady work we're happy to show you.
            </p>
          </div>
        )}

        {/* Owner intro */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          {!isHome && (
            <div className="inline-flex items-center gap-2 text-[var(--brand-gold)] mb-3 justify-center">
              <span className="gold-divider" />
              <span className="section-eyebrow">Meet the Team</span>
              <span className="gold-divider" />
            </div>
          )}
          <h3 className={`${isHome ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"} font-extrabold text-[var(--brand-teal)] leading-tight`} style={{ fontFamily: "Outfit" }}>
            A local team you'll actually recognize.
          </h3>
          <div className="mt-5 space-y-4 text-slate-700 leading-relaxed">
            <p>
              Top-Notch Pro Solutions is a family-run home services business based right here in Evansville, IN.
              We started out helping neighbors with everything from move-out cleans to weekend deck repairs — and
              we've grown by doing what most companies forget: showing up on time, doing what we said we'd do, and
              treating your home with respect.
            </p>
            <p>
              Whether it's a routine house cleaning or a full weekend project like new flooring or rebuilding a fence
              line, you'll work with the same small crew every time — no rotating strangers, no rushed jobs, no cutting
              corners.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/quote" className="btn-accent" data-testid="local-trust-cta-book">
              Book Cleaning Online <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/request-quote" className="btn-outline" data-testid="local-trust-cta-quote">
              Get a Handyman Quote
            </Link>
          </div>
          <div className="mt-4 text-xs text-slate-500 italic">
            Locally owned & operated · Evansville, IN
          </div>
        </div>

        {/* Recent work gallery */}
        <div>
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="inline-flex items-center gap-2 text-[var(--brand-gold)] mb-2">
                <span className="gold-divider" />
                <span className="section-eyebrow">Recent Work</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-[var(--brand-teal)] leading-tight" style={{ fontFamily: "Outfit" }}>
                A few of our latest projects
              </h3>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Star className="w-3.5 h-3.5 text-[var(--brand-gold)] fill-[var(--brand-gold)]" />
              <span>Photos from real jobs — no stock imagery.</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {RECENT_WORK.map((project) => {
              const hasPhotos = project.photos && project.photos.length > 0;
              const Wrapper = hasPhotos ? "button" : "article";
              return (
                <Wrapper
                  key={project.id}
                  type={hasPhotos ? "button" : undefined}
                  onClick={hasPhotos ? () => setOpenProject(project) : undefined}
                  aria-label={hasPhotos ? `Open ${project.title} photo gallery` : undefined}
                  className={`group rounded-2xl overflow-hidden border border-[#e6e0d4] bg-white shadow-sm hover:shadow-lg transition text-left w-full ${hasPhotos ? "cursor-pointer" : ""}`}
                  data-testid={`recent-work-${project.id}`}
                >
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <PhotoTile photos={project.photos} alt={project.title} testid={`recent-work-photo-${project.id}`} />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/95 text-[10px] font-bold uppercase tracking-wider text-[var(--brand-teal)] shadow-sm">
                      <project.icon className="w-3 h-3" />
                      Project
                    </div>
                    {hasPhotos && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="px-3 py-1.5 rounded-full bg-white text-[var(--brand-teal)] font-bold text-xs uppercase tracking-wider shadow-lg">
                          View all photos
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h4 className="font-bold text-[var(--brand-teal)] leading-tight" style={{ fontFamily: "Outfit" }}>
                      {project.title}
                    </h4>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{project.blurb}</p>
                  </div>
                </Wrapper>
              );
            })}
          </div>

          <p className="mt-5 text-xs text-slate-400 italic text-center">
            Have a project you'd like to see done like this?{" "}
            <Link to="/request-quote" className="text-[var(--brand-teal)] font-semibold underline not-italic">Request a quote</Link>
            {" "}and we'll walk you through it.
          </p>
        </div>
      </div>

      <ProjectLightbox project={openProject} onClose={() => setOpenProject(null)} />
    </section>
  );
}
